import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import AnnoSelector from "@/components/AnnoSelector";
import AlunniSendBar from "@/components/AlunniSendBar";
import useAlunniSelection from "@/lib/useAlunniSelection";

/**
 * Pagina "Deleghe Ritiro" (Primaria — settori E e F).
 *
 * Matrice alunni × 10 slot di delegati (testo libero).
 * Quando si scrive un nome viene salvato con data/ora di compilazione.
 *
 * Richiesta utente 2026-02-17.
 */
const NUM_SLOTS = 14;

const fmtItDateTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch (e) { return ""; }
};

const fmtItDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch (e) { return ""; }
};

const isScaduto = (scad) => {
  if (!scad) return false;
  try {
    const d = new Date(scad);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return d < today;
  } catch (e) { return false; }
};

export default function DelegheRitiroPage() {
  const { annoCorrente } = useAuth();
  const { gestione } = useParams(); // eslint-disable-line no-unused-vars
  const [data, setData] = useState({ anno: null, items: [], num_slots: NUM_SLOTS });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");
  const [filterSet, setFilterSet] = useState("");
  const [filterCla, setFilterCla] = useState("");
  const [sortMode, setSortMode] = useState("settore_classe"); // "settore_classe" | "cognome"
  const [filtroAttivi, setFiltroAttivi] = useState(""); // "" (tutti) | "attivi" (con almeno un delegato)
  const [editing, setEditing] = useState(null); // {cod, slot, value}
  const [qrOpen, setQrOpen] = useState(false);
  // 🆕 Feb 2026: anno scolastico "condiviso" via QR (default = anno visualizzato)
  const [qrAnno, setQrAnno] = useState(null);
  // 🆕 Feb 2026: import massivo XLSX
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importOverwrite, setImportOverwrite] = useState(false);
  // 🆕 Giu 2026: import "incolla" da schermata Spaggiari (match per cognome+nome)
  const [incollaOpen, setIncollaOpen] = useState(false);
  const [incollaText, setIncollaText] = useState("");
  const [incollaPreview, setIncollaPreview] = useState(null);
  const [incollaBusy, setIncollaBusy] = useState(false);
  const [incollaOverwrite, setIncollaOverwrite] = useState(false);
  // 🆕 Giu 2026: import Excel/CSV (Classe, Alunno, Delegato) — match per cognome+nome
  const [cadOpen, setCadOpen] = useState(false);
  const [cadFile, setCadFile] = useState(null);
  const [cadPreview, setCadPreview] = useState(null);
  const [cadBusy, setCadBusy] = useState(false);
  const [cadOverwrite, setCadOverwrite] = useState(false);
  const fileInputRef = React.useRef(null);
  const sel = useAlunniSelection();
  const publicUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    const base = `${window.location.origin}/insegnanti`;
    const yr = qrAnno || data.anno || annoCorrente;
    return yr ? `${base}?anno=${yr}` : base;
  }, [qrAnno, data.anno, annoCorrente]);
  // Sincronizza qrAnno con l'anno caricato quando il modal è chiuso
  useEffect(() => {
    if (!qrOpen && data.anno) setQrAnno(data.anno);
  }, [qrOpen, data.anno]);

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const params = annoCorrente ? { anno: annoCorrente } : {};
      const r = await api.get("/deleghe-ritiro/matrice", { params });
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore caricamento");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [annoCorrente]);

  const setoriList = useMemo(() => {
    const s = new Set(data.items.map(it => it.settore).filter(Boolean));
    return Array.from(s).sort();
  }, [data.items]);
  const classiList = useMemo(() => {
    const s = new Set(data.items.filter(it => !filterSet || it.settore === filterSet).map(it => it.classe).filter(Boolean));
    return Array.from(s).sort();
  }, [data.items, filterSet]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = data.items.filter(it => {
      if (filterSet && it.settore !== filterSet) return false;
      if (filterCla && it.classe !== filterCla) return false;
      if (q && !(`${it.Cog} ${it.Nom}`.toLowerCase().includes(q) || String(it.Cod).includes(q))) return false;
      if (filtroAttivi === "attivi") {
        const has = (it.deleghe || []).some(d => (d?.nome || "").trim());
        if (!has) return false;
      }
      return true;
    });
    const cmpStr = (a, b) => (a || "").localeCompare(b || "", "it", { sensitivity: "base" });
    if (sortMode === "cognome") {
      return [...list].sort((a, b) => cmpStr(a.Cog, b.Cog) || cmpStr(a.Nom, b.Nom));
    }
    return [...list].sort((a, b) =>
      cmpStr(a.settore, b.settore) ||
      cmpStr(a.classe, b.classe) ||
      cmpStr(a.Cog, b.Cog) ||
      cmpStr(a.Nom, b.Nom)
    );
  }, [data.items, search, filterSet, filterCla, sortMode, filtroAttivi]);

  const saveCell = async () => {
    if (!editing) return;
    const { cod, slot } = editing;
    const nome = (editing.nome || "").trim();
    const documento = (editing.documento || "").trim();
    const scadenza = (editing.scadenza || "").trim();
    try {
      const r = await api.put(`/deleghe-ritiro/${cod}/slot/${slot}`,
        { nome, documento, scadenza },
        { params: annoCorrente ? { anno: annoCorrente } : {} });
      setData(d => ({
        ...d,
        items: d.items.map(it => {
          if (it.Cod !== cod) return it;
          const newDel = [...(it.deleghe || Array(NUM_SLOTS).fill(null))];
          if (r.data.deleted) {
            newDel[slot] = null;
          } else {
            newDel[slot] = {
              nome: r.data.nome,
              documento: r.data.documento,
              scadenza: r.data.scadenza,
              updated_at: r.data.updated_at,
              updated_by: r.data.updated_by,
            };
          }
          return { ...it, deleghe: newDel };
        }),
      }));
      setEditing(null);
    } catch (e) {
      alert("Errore salvataggio: " + (e.response?.data?.detail || e.message));
    }
  };

  // 🆕 Feb 2026: gestisce upload XLSX per import massivo deleghe
  const handleImportXlsx = async (evt) => {
    const f = evt.target.files?.[0];
    evt.target.value = "";
    if (!f) return;
    setImporting(true); setImportResult(null); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const params = {};
      if (annoCorrente) params.anno = annoCorrente;
      if (importOverwrite) params.overwrite = true;
      const r = await api.post("/deleghe-ritiro/import-xlsx", fd, {
        params,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(r.data);
      await load();
    } catch (e) {
      setErr("Errore import: " + (e.response?.data?.detail || e.message));
    } finally {
      setImporting(false);
    }
  };

  // 🆕 Giu 2026: anteprima / import "incolla" da Spaggiari
  const runIncolla = async (dryRun) => {
    if (!incollaText.trim()) { setErr("Incolla prima i dati dalla schermata Spaggiari."); return; }
    setIncollaBusy(true); setErr("");
    try {
      const r = await api.post("/deleghe-ritiro/import-incolla", {
        testo: incollaText,
        anno: data.anno || annoCorrente,
        overwrite: incollaOverwrite,
        dry_run: dryRun,
      });
      setIncollaPreview(r.data);
      if (!dryRun) { await load(); }
    } catch (e) {
      setErr("Errore import incolla: " + (e.response?.data?.detail || e.message));
    } finally {
      setIncollaBusy(false);
    }
  };

  // 🆕 Giu 2026: anteprima / import da file Excel/CSV (Classe, Alunno, Delegato)
  const runCad = async (dryRun) => {
    if (!cadFile) { setErr("Seleziona prima un file Excel/CSV."); return; }
    setCadBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", cadFile);
      const params = { dry_run: dryRun };
      if (data.anno || annoCorrente) params.anno = data.anno || annoCorrente;
      if (cadOverwrite) params.overwrite = true;
      const r = await api.post("/deleghe-ritiro/import-cad", fd, {
        params,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCadPreview(r.data);
      if (!dryRun) { await load(); }
    } catch (e) {
      setErr("Errore import Excel: " + (e.response?.data?.detail || e.message));
    } finally {
      setCadBusy(false);
    }
  };

  // 🆕 Feb 2026: duplica un singolo delegato nell'anno successivo
  const duplicaAnnoSuccessivo = async (cod, slot, nome) => {
    if (!nome) return;
    const annoAtt = data.anno || annoCorrente;
    if (!annoAtt) { alert("Anno scolastico non definito"); return; }
    const annoNext = Number(annoAtt) + 1;
    if (!window.confirm(`Duplicare "${nome}" nell'anno ${annoNext}/${String((annoNext + 1)).slice(-2)}?\n(Verrà scritto sullo stesso slot Delegato ${slot + 1}, sovrascrivendo se già presente)`)) return;
    try {
      await api.put(`/deleghe-ritiro/${cod}/slot/${slot}`, { nome }, { params: { anno: annoNext } });
      alert(`✓ "${nome}" duplicato nell'anno ${annoNext}/${String((annoNext + 1)).slice(-2)} (Delegato ${slot + 1}).`);
    } catch (e) {
      alert("Errore duplicazione: " + (e.response?.data?.detail || e.message));
    }
  };

  // Cella singola delegato (riusata per le 2 righe: slot 0-6 sopra, 7-13 sotto)
  const slotCell = (it, slot) => {
    const d = it.deleghe?.[slot];
    const isEditing = editing && editing.cod === it.Cod && editing.slot === slot;
    const scaduto = isScaduto(d?.scadenza);
    return (
      <td key={slot} style={{ padding: 2, verticalAlign: "top", position: "relative" }}>
        {isEditing ? (
          <div
            data-testid={`deleghe-editor-${it.Cod}-${slot}`}
            style={{
              position: "absolute", top: 0, left: 0, zIndex: 50,
              width: 210, background: "white", padding: 8,
              border: "2px solid #0d47a1", borderRadius: 5,
              boxShadow: "0 6px 24px rgba(0,0,0,0.25)",
              display: "flex", flexDirection: "column", gap: 6,
            }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: "#0d47a1" }}>
              Delegato {slot + 1}
            </div>
            <input
              data-testid={`deleghe-input-nome-${it.Cod}-${slot}`}
              type="text" autoFocus placeholder="Nome delegato"
              value={editing.nome}
              onChange={(e) => setEditing(s => ({ ...s, nome: e.target.value }))}
              onKeyDown={(e) => { if (e.key === "Escape") setEditing(null); }}
              style={{ width: "100%", padding: "5px 6px", fontSize: 12, border: "1px solid #cfd8dc", borderRadius: 3, boxSizing: "border-box", outline: "none" }}
            />
            <input
              data-testid={`deleghe-input-documento-${it.Cod}-${slot}`}
              type="text" placeholder="Documento (es. C.I. AY123)"
              value={editing.documento}
              onChange={(e) => setEditing(s => ({ ...s, documento: e.target.value }))}
              onKeyDown={(e) => { if (e.key === "Escape") setEditing(null); }}
              style={{ width: "100%", padding: "5px 6px", fontSize: 12, border: "1px solid #cfd8dc", borderRadius: 3, boxSizing: "border-box", outline: "none" }}
            />
            <label style={{ fontSize: 9, color: "#666", fontWeight: 700 }}>Scadenza documento</label>
            <input
              data-testid={`deleghe-input-scadenza-${it.Cod}-${slot}`}
              type="date"
              value={editing.scadenza}
              onChange={(e) => setEditing(s => ({ ...s, scadenza: e.target.value }))}
              onKeyDown={(e) => { if (e.key === "Escape") setEditing(null); }}
              style={{ width: "100%", padding: "5px 6px", fontSize: 12, border: "1px solid #cfd8dc", borderRadius: 3, boxSizing: "border-box", outline: "none" }}
            />
            <div style={{ display: "flex", gap: 6, marginTop: 2 }}>
              <button
                data-testid={`deleghe-save-${it.Cod}-${slot}`}
                onClick={saveCell}
                style={{ flex: 1, padding: "5px 0", background: "#2e7d32", color: "white", border: "none", borderRadius: 3, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                💾 Salva
              </button>
              <button
                data-testid={`deleghe-cancel-${it.Cod}-${slot}`}
                onClick={() => setEditing(null)}
                style={{ padding: "5px 8px", background: "#eceff1", color: "#555", border: "none", borderRadius: 3, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                ✕
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setEditing({ cod: it.Cod, slot, nome: d?.nome || "", documento: d?.documento || "", scadenza: d?.scadenza || "" })}
            data-testid={`deleghe-cell-${it.Cod}-${slot}`}
            title={d?.nome ? `${d.nome}${d?.documento ? ` — doc: ${d.documento}` : ""}${d?.scadenza ? ` — scad. ${fmtItDate(d.scadenza)}` : ""}${d?.updated_at ? ` — inserito il ${fmtItDateTime(d.updated_at)}` : ""}${d?.updated_by ? ` da ${d.updated_by}` : ""}` : "Clicca per inserire il nome del delegato"}
            style={{
              width: "100%", minHeight: 34, padding: "3px 5px",
              background: d?.nome ? (scaduto ? "#c62828" : "#2e7d32") : "transparent",
              border: `1px dashed ${d?.nome ? (scaduto ? "#8e0000" : "#1b5e20") : "#cfd8dc"}`,
              borderRadius: 3,
              textAlign: "left", cursor: "text",
              display: "flex", flexDirection: "column", justifyContent: "center",
              alignItems: "flex-start", overflow: "hidden",
            }}>
            {d?.nome ? (
              <>
                <span style={{ fontSize: 11, fontWeight: 700, color: "white", lineHeight: 1.15, width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {d.nome}
                </span>
                {d.documento && (
                  <span style={{ fontSize: 9, color: "#e8f5e9", lineHeight: 1.15, marginTop: 1, width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    📄 {d.documento}
                  </span>
                )}
                {d.scadenza && (
                  <span style={{ fontSize: 9, color: scaduto ? "#ffcdd2" : "#c8e6c9", fontWeight: scaduto ? 800 : 400, lineHeight: 1.15, marginTop: 1 }}>
                    ⏱ {scaduto ? "SCADUTO " : "scad. "}{fmtItDate(d.scadenza)}
                  </span>
                )}
                <span style={{ fontSize: 9, color: "#c8e6c9", lineHeight: 1.15, marginTop: 1, fontStyle: "italic" }}>
                  {fmtItDateTime(d.updated_at)}
                </span>
              </>
            ) : (
              <span style={{ fontSize: 10, color: "#90a4ae", fontStyle: "italic" }}>+ aggiungi</span>
            )}
          </button>
        )}
        {d?.nome && !isEditing && (
          <button
            onClick={(e) => { e.stopPropagation(); duplicaAnnoSuccessivo(it.Cod, slot, d.nome); }}
            data-testid={`deleghe-dup-next-${it.Cod}-${slot}`}
            title={`Duplica "${d.nome}" nell'anno successivo (stesso slot Delegato ${slot + 1})`}
            style={{
              position: "absolute", top: 3, right: 3,
              background: "rgba(255,255,255,0.25)", color: "white",
              border: "1px solid rgba(255,255,255,0.5)", borderRadius: 3,
              padding: "0 4px", fontSize: 10, fontWeight: 800,
              lineHeight: 1.3, cursor: "pointer",
            }}>
            ↗+1
          </button>
        )}
      </td>
    );
  };


  return (
    <div data-testid="deleghe-ritiro-page" style={{ padding: "12px 20px" }}>
      <div className="sw-page-title">
        <h1>👥 Deleghe Ritiro Alunni</h1>
        <div className="sw-breadcrumb">Home &gt; Deleghe Ritiro</div>
        {/* 🆕 Feb 2026: bottone Importa XLSX */}
        <input ref={fileInputRef} type="file" accept=".xlsx"
          data-testid="deleghe-import-file"
          onChange={handleImportXlsx}
          style={{ display: "none" }} />
        <label style={{
          marginLeft: "auto",
          display: "inline-flex", alignItems: "center", gap: 4,
          fontSize: 10, color: "#666", cursor: "pointer",
        }} title="Se attivo, cancella le deleghe esistenti dell'alunno prima di importare quelle nuove (per gli alunni presenti nel file)">
          <input type="checkbox" checked={importOverwrite}
            data-testid="deleghe-import-overwrite"
            onChange={e => setImportOverwrite(e.target.checked)} />
          Sovrascrivi
        </label>
        <button onClick={() => fileInputRef.current?.click()}
          disabled={importing}
          data-testid="deleghe-import-btn"
          title="Importa un file .xlsx con schema Segremat Web (id alunno in colonna 3, nomi delegati da colonna 5)"
          style={{ marginLeft: 6, padding: "7px 14px", background: importing ? "#9e9e9e" : "#00695c", color: "white", border: "none", borderRadius: 3, fontSize: 11, fontWeight: 700, cursor: importing ? "wait" : "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
          {importing ? "⏳ Import…" : "📥 Importa XLSX"}
        </button>
        <button onClick={() => { setIncollaOpen(true); setIncollaPreview(null); }}
          data-testid="deleghe-incolla-btn"
          title="Importa le deleghe copiate-incollate dalla schermata Spaggiari, abbinandole all'alunno per cognome e nome (Primaria + Infanzia: A/E/F)"
          style={{ marginLeft: 6, padding: "7px 14px", background: "#ad1457", color: "white", border: "none", borderRadius: 3, fontSize: 11, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
          📋 Incolla da Spaggiari
        </button>
        <button onClick={() => { setCadOpen(true); setCadPreview(null); setCadFile(null); }}
          data-testid="deleghe-cad-btn"
          title="Importa un file Excel/CSV con colonne (Classe, Alunno, Delegato), abbinando l'alunno per cognome e nome (Primaria + Infanzia: A/E/F)"
          style={{ marginLeft: 6, padding: "7px 14px", background: "#4527a0", color: "white", border: "none", borderRadius: 3, fontSize: 11, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
          📊 Importa Excel (Classe/Alunno/Delegato)
        </button>
        <button onClick={() => {
            const base = process.env.REACT_APP_BACKEND_URL;
            const p = new URLSearchParams();
            if (annoCorrente) p.set("anno", annoCorrente);
            if (filterSet) p.set("settore", filterSet);
            if (filterCla) p.set("classe", filterCla);
            if (search.trim()) p.set("q", search.trim());
            if (filtroAttivi === "attivi") p.set("attivi", "1");
            window.open(`${base}/api/deleghe-ritiro/stampa?${p.toString()}`, "_blank", "noopener,noreferrer");
          }}
          data-testid="deleghe-stampa-btn"
          title="Stampa l'elenco attualmente visualizzato, con i filtri applicati (PDF A3 orizzontale)"
          style={{ marginLeft: 6, padding: "7px 14px", background: "#4a148c", color: "white", border: "none", borderRadius: 3, fontSize: 11, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
          🖨 Stampa elenco
        </button>
        <button onClick={() => setQrOpen(true)}
          data-testid="deleghe-qr-btn"
          title="Mostra QR Code per Portale Insegnanti (accesso libero in sola lettura)"
          style={{ marginLeft: 6, padding: "7px 14px", background: "#0d47a1", color: "white", border: "none", borderRadius: 3, fontSize: 11, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
          📱 QR Insegnanti
        </button>
      </div>

      {/* 🆕 Feb 2026: banner risultato import XLSX */}
      {importResult && (
        <div style={{ margin: "10px 0", padding: 10, background: "#e8f5e9", border: "1px solid #81c784", borderRadius: 4, fontSize: 12 }}>
          <div style={{ fontWeight: 800, color: "#1b5e20", marginBottom: 4 }}>
            ✓ Import completato:
            {" "}<b>{importResult.slot_importati}</b> slot delega scritti su
            {" "}<b>{importResult.totale_righe_valide}</b> alunni.
            <button onClick={() => setImportResult(null)} style={{ marginLeft: 12, background: "transparent", border: "none", color: "#1b5e20", cursor: "pointer", fontWeight: 700 }}>✕ chiudi</button>
          </div>
          {importResult.count_alunno_non_trovato > 0 && (
            <div style={{ color: "#bf360c" }}>
              ⚠ <b>{importResult.count_alunno_non_trovato}</b> alunni presenti nel file ma non trovati nell&apos;anno selezionato.
            </div>
          )}
          {importResult.count_troncati > 0 && (
            <div style={{ color: "#e65100" }}>
              ⚠ <b>{importResult.count_troncati}</b> alunni avevano più di {NUM_SLOTS} nomi: solo i primi {NUM_SLOTS} sono stati importati (limite slot).
            </div>
          )}
          {importResult.righe_senza_nomi > 0 && (
            <div style={{ color: "#666" }}>
              {importResult.righe_senza_nomi} righe senza nomi delegati (ignorate).
            </div>
          )}
          {importResult.count_errori > 0 && (
            <div style={{ color: "#c62828" }}>
              ✗ <b>{importResult.count_errori}</b> errori (i primi 50 sono nella response API).
            </div>
          )}
        </div>
      )}

      <div className="sw-card">
        <div className="sw-card-body">
          <div className="sw-filter-row" style={{ flexWrap: "wrap", gap: 8 }}>
            <AnnoSelector testId="deleghe-anno-attivo" />
            <span className="sw-filter-label" style={{ marginLeft: 8 }}>Cerca:</span>
            <input data-testid="deleghe-search" type="text" placeholder="cognome, nome, cod…"
              value={search} onChange={e => setSearch(e.target.value)}
              className="sw-filter-input" style={{ width: 200 }} />
            <span className="sw-filter-label" style={{ marginLeft: 8 }}>Settore:</span>
            <select className="sw-filter-select" value={filterSet}
              onChange={e => { setFilterSet(e.target.value); setFilterCla(""); }}
              data-testid="deleghe-filter-settore" style={{ minWidth: 100 }}>
              <option value="">Tutti (A + E + F)</option>
              {setoriList.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <span className="sw-filter-label" style={{ marginLeft: 8 }}>Classe:</span>
            <select className="sw-filter-select" value={filterCla}
              onChange={e => setFilterCla(e.target.value)} style={{ minWidth: 100 }}>
              <option value="">Tutte</option>
              {classiList.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <span className="sw-filter-label" style={{ marginLeft: 8 }}>Mostra:</span>
            <select data-testid="deleghe-filter-attivi" value={filtroAttivi} onChange={e => setFiltroAttivi(e.target.value)}
              title="Attivi = alunni con almeno un delegato registrato"
              className="sw-filter-select"
              style={{ minWidth: 110, background: filtroAttivi ? "#fff59d" : "white", fontWeight: filtroAttivi ? 700 : 400 }}>
              <option value="">Tutti</option>
              <option value="attivi">Solo con dati inseriti</option>
            </select>
            {(search || filterSet || filterCla || filtroAttivi) && (
              <button className="sw-filter-btn" onClick={() => { setSearch(""); setFilterSet(""); setFilterCla(""); setFiltroAttivi(""); }}>
                Pulisci
              </button>
            )}
            <span className="sw-filter-label" style={{ marginLeft: 8 }}>Ordina:</span>
            <select className="sw-filter-select" value={sortMode} onChange={e => setSortMode(e.target.value)}
              data-testid="deleghe-sort-select" style={{ minWidth: 180 }}>
              <option value="settore_classe">Settore · Classe · Cognome Nome</option>
              <option value="cognome">Cognome · Nome</option>
            </select>
            <span style={{ marginLeft: "auto", fontSize: 11, color: "#444" }}>
              <b>{filtered.length}</b> alunni · <b>{NUM_SLOTS}</b> slot delega per alunno
            </span>
          </div>
          <div style={{ marginTop: 6, fontSize: 11, color: "#555", lineHeight: 1.4 }}>
            Vista limitata ai settori <b>A</b> (Infanzia), <b>E</b> ed <b>F</b> (Primaria). Clicca su una cella per inserire o modificare
            <b> nome del delegato, documento e scadenza</b>; data e ora di compilazione vengono registrate automaticamente.
            Le celle con documento <b style={{ color: "#c62828" }}>scaduto</b> vengono evidenziate in rosso.
          </div>
        </div>
      </div>

      {err && <div style={{ padding: 10, background: "#ffebee", color: "#c62828", border: "1px solid #ef9a9a", margin: "8px 0", fontSize: 12 }}>{err}</div>}

      <div className="sw-card" style={{ marginTop: 8 }}>
        <div style={{ overflow: "auto", maxHeight: "75vh" }}>
          <AlunniSendBar selected={sel.selected} onClear={sel.clear}
            label={`Deleghe Ritiro ${data.anno ? `${data.anno}/${String((data.anno||0)+1).slice(-2)}` : ""}`}
            anno={data.anno} />
          <table className="sw-table" style={{ width: "100%", tableLayout: "fixed" }}>
            <thead className="thead-blue">
              <tr>
                <th style={{ width: 28, textAlign: "center" }}>
                  <input type="checkbox" data-testid="deleghe-selall"
                    title="Seleziona/Deseleziona tutti i visibili"
                    checked={filtered.length > 0 && filtered.every(it => sel.isSelected(it.Cod))}
                    onChange={(e) => e.target.checked ? sel.selectAll(filtered.map(it => it.Cod)) : sel.deselectAll(filtered.map(it => it.Cod))} />
                </th>
                <th style={{ width: 32, textAlign: "center" }}>#</th>
                <th style={{ width: 52, textAlign: "center" }}>Cod</th>
                <th style={{ width: 150 }}>Cognome Nome</th>
                <th style={{ width: 36, textAlign: "center" }}>S.</th>
                <th style={{ width: 36, textAlign: "center" }}>Cl.</th>
                {Array.from({ length: 7 }, (_, i) => (
                  <th key={i} style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 10, fontWeight: 700 }}>Del. {i + 1} / {i + 8}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6 + 7} className="sw-loading">Caricamento…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6 + 7} className="sw-empty"><p>Nessun alunno trovato nei settori A/E/F per l&apos;anno selezionato</p></td></tr>
              ) : filtered.map((it, i) => {
                const leftBorder = { borderTop: "2px solid #b39ddb" };
                return (
                <React.Fragment key={it.Cod}>
                  <tr data-testid={`deleghe-row-${it.Cod}`}
                      style={{ background: sel.isSelected(it.Cod) ? "#f3e5f5" : undefined }}>
                    <td rowSpan={2} style={{ textAlign: "center", ...leftBorder }}>
                      <input type="checkbox" data-testid={`deleghe-sel-${it.Cod}`}
                        checked={sel.isSelected(it.Cod)} onChange={() => sel.toggle(it.Cod)} />
                    </td>
                    <td rowSpan={2} style={{ color: "#888", textAlign: "center", fontSize: 11, ...leftBorder }}>{i + 1}</td>
                    <td rowSpan={2} style={{ textAlign: "center", fontFamily: "Consolas", fontSize: 11, ...leftBorder }}>{it.Cod}</td>
                    <td rowSpan={2} style={{ fontWeight: 600, fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", ...leftBorder }} title={`${it.Cog} ${it.Nom}`}>{it.Cog} {it.Nom}</td>
                    <td rowSpan={2} style={{ textAlign: "center", fontWeight: 600, fontSize: 11, ...leftBorder }}>{it.settore || "-"}</td>
                    <td rowSpan={2} style={{ textAlign: "center", fontWeight: 600, fontSize: 11, ...leftBorder }}>{it.classe || "-"}</td>
                    {Array.from({ length: 7 }, (_, k) => slotCell(it, k))}
                  </tr>
                  <tr data-testid={`deleghe-row-${it.Cod}-b`}
                      style={{ background: sel.isSelected(it.Cod) ? "#f3e5f5" : undefined }}>
                    {Array.from({ length: 7 }, (_, k) => slotCell(it, k + 7))}
                  </tr>
                </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {qrOpen && (
        <div onClick={() => setQrOpen(false)} data-testid="deleghe-qr-modal"
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: "white", borderRadius: 8, padding: 28,
            maxWidth: 460, width: "90%", textAlign: "center",
            boxShadow: "0 12px 40px rgba(0,0,0,0.3)",
          }}>
            <h2 style={{ margin: "0 0 6px 0", color: "#0d47a1", fontSize: 18 }}>📱 Portale Insegnanti</h2>
            <div style={{ fontSize: 12, color: "#666", marginBottom: 14 }}>
              Inquadra il QR code per accedere alla pagina pubblica<br />
              (consultazione delegati al ritiro · accesso libero)
            </div>
            {/* 🆕 Feb 2026: selettore anno per le deleghe visibili dagli insegnanti */}
            <div style={{ marginBottom: 14, padding: "8px 10px", background: "#e3f2fd", border: "1px solid #90caf9", borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontSize: 12 }}>
              <label htmlFor="qr-anno-select" style={{ color: "#0d47a1", fontWeight: 700 }}>
                Anno scolastico visibile:
              </label>
              <select id="qr-anno-select"
                data-testid="deleghe-qr-anno-select"
                value={qrAnno || data.anno || ""}
                onChange={e => setQrAnno(Number(e.target.value))}
                style={{ padding: "4px 8px", border: "1px solid #0d47a1", borderRadius: 3, fontSize: 12, fontWeight: 800, background: "white", color: "#0d47a1", cursor: "pointer" }}>
                {(() => {
                  const currentYear = data.anno || annoCorrente || new Date().getFullYear();
                  const years = [];
                  for (let y = currentYear + 2; y >= currentYear - 5; y--) years.push(y);
                  return years.map(y => (
                    <option key={y} value={y}>{y}/{String((y + 1) % 100).padStart(2, "0")}</option>
                  ));
                })()}
              </select>
            </div>
            <div style={{ display: "inline-block", padding: 14, background: "white", border: "3px solid #0d47a1", borderRadius: 8 }}>
              <QRCodeSVG value={publicUrl} size={240} level="M" includeMargin={false} />
            </div>
            <div style={{ marginTop: 14, padding: "8px 12px", background: "#f5f5f5", borderRadius: 4, fontSize: 12, fontFamily: "Consolas, monospace", color: "#333", wordBreak: "break-all" }}>
              {publicUrl}
            </div>
            <div style={{ fontSize: 11, color: "#888", marginTop: 12, lineHeight: 1.5 }}>
              Gli insegnanti che scansionano questo QR vedranno le deleghe dell&apos;anno <b>{qrAnno ? `${qrAnno}/${String((qrAnno + 1) % 100).padStart(2, "0")}` : "—"}</b>.
              <br />Puoi cambiare l&apos;anno sopra e ricondividere il nuovo QR.
            </div>
            <button onClick={() => setQrOpen(false)} data-testid="deleghe-qr-close"
              style={{ marginTop: 16, padding: "8px 22px", background: "#0d47a1", color: "white", border: "none", borderRadius: 4, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
              Chiudi
            </button>
          </div>
        </div>
      )}

      {/* 🆕 Giu 2026: modale "Incolla da Spaggiari" */}
      {incollaOpen && (
        <div data-testid="deleghe-incolla-modal" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 6000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 24, overflow: "auto" }}
          onClick={(e) => { if (e.target === e.currentTarget) setIncollaOpen(false); }}>
          <div style={{ background: "white", borderRadius: 10, width: "min(920px, 96vw)", maxHeight: "92vh", overflow: "auto", boxShadow: "0 12px 40px rgba(0,0,0,0.3)" }}>
            <div style={{ position: "sticky", top: 0, background: "#ad1457", color: "white", padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center", borderTopLeftRadius: 10, borderTopRightRadius: 10 }}>
              <h3 style={{ margin: 0, fontSize: 16 }}>📋 Incolla deleghe da Spaggiari — Primaria + Infanzia (A/E/F)</h3>
              <button onClick={() => setIncollaOpen(false)} data-testid="deleghe-incolla-close" style={{ background: "transparent", border: "none", color: "white", fontSize: 20, cursor: "pointer", lineHeight: 1 }}>✕</button>
            </div>
            <div style={{ padding: 18 }}>
              <p style={{ margin: "0 0 8px", fontSize: 12, color: "#546e7a", lineHeight: 1.5 }}>
                Vai su Spaggiari → <b>Riepilogo deleghe</b>, seleziona le righe (anche più classi insieme) e incollale qui sotto.
                <b> Importante:</b> includi nella selezione il link <b>“Modifica deleghe”</b>: serve a separare l'alunno dai suoi delegati.
                Gli alunni vengono abbinati per <b>cognome + nome</b> (Primaria + Infanzia: A/E/F). Ogni alunno può avere fino a <b>{NUM_SLOTS} delegati</b>.
                Le righe di dettaglio (documento, numero, parentela, telefono, “Abilitata”) vengono ignorate automaticamente.
              </p>
              <textarea
                data-testid="deleghe-incolla-textarea"
                value={incollaText}
                onChange={(e) => { setIncollaText(e.target.value); setIncollaPreview(null); }}
                placeholder={"FOGAZZI ALESSANDRO Modifica deleghe FERRARI VALTER\nDELEGATO  Carta d'identità\nCA73160XP\nAbilitata\n  ZINI MARINA\nDELEGATO  Carta d'identità\n..."}
                style={{ width: "100%", minHeight: 140, boxSizing: "border-box", padding: 10, fontFamily: "Consolas, monospace", fontSize: 12, border: "1px solid #cfd8dc", borderRadius: 6, resize: "vertical" }} />

              <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "10px 0", flexWrap: "wrap" }}>
                <button data-testid="deleghe-incolla-preview-btn" onClick={() => runIncolla(true)} disabled={incollaBusy || !incollaText.trim()}
                  style={{ padding: "8px 16px", background: "#1565c0", color: "white", border: "none", borderRadius: 4, fontSize: 12, fontWeight: 700, cursor: incollaBusy ? "wait" : "pointer" }}>
                  {incollaBusy ? "⏳…" : "🔍 Anteprima abbinamenti"}
                </button>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, color: "#455a64" }}
                  title="Se attivo, cancella le deleghe esistenti dell'alunno prima di importare quelle nuove">
                  <input type="checkbox" data-testid="deleghe-incolla-overwrite" checked={incollaOverwrite} onChange={(e) => setIncollaOverwrite(e.target.checked)} />
                  Sovrascrivi deleghe esistenti
                </label>
              </div>

              {incollaPreview && (
                <div data-testid="deleghe-incolla-preview" style={{ marginTop: 6 }}>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10, fontSize: 12 }}>
                    <span style={{ background: "#e8f5e9", color: "#1b5e20", padding: "4px 10px", borderRadius: 20, fontWeight: 700 }}>✓ Abbinati: {incollaPreview.count_abbinati}</span>
                    <span style={{ background: "#fff8e1", color: "#e65100", padding: "4px 10px", borderRadius: 20, fontWeight: 700 }}>⚠ Ambigui: {incollaPreview.count_ambigui}</span>
                    <span style={{ background: "#ffebee", color: "#b71c1c", padding: "4px 10px", borderRadius: 20, fontWeight: 700 }}>✗ Non trovati: {incollaPreview.count_non_trovati}</span>
                  </div>

                  <div style={{ maxHeight: "38vh", overflow: "auto", border: "1px solid #eceff1", borderRadius: 6 }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                      <thead style={{ position: "sticky", top: 0, background: "#37474f", color: "white" }}>
                        <tr>
                          <th style={{ padding: 6, textAlign: "left" }}>Studente (incollato)</th>
                          <th style={{ padding: 6, textAlign: "left" }}>Abbinato a</th>
                          <th style={{ padding: 6, textAlign: "center" }}>Classe</th>
                          <th style={{ padding: 6, textAlign: "left" }}>Delegati</th>
                        </tr>
                      </thead>
                      <tbody>
                        {incollaPreview.abbinati.map((a, i) => (
                          <tr key={`ok-${i}`} style={{ borderTop: "1px solid #eceff1", background: "#f1f8e9" }}>
                            <td style={{ padding: 6 }}>{a.studente}</td>
                            <td style={{ padding: 6, fontWeight: 600, color: "#1b5e20" }}>{a.cog} {a.nom}</td>
                            <td style={{ padding: 6, textAlign: "center" }}>{a.settore}{a.classe}</td>
                            <td style={{ padding: 6 }}>
                              {a.delegati.join(", ")}
                              {a.troncati && a.troncati.length > 0 && (
                                <span style={{ color: "#e65100", fontWeight: 700 }}> · ⚠ scartati (oltre {NUM_SLOTS}): {a.troncati.join(", ")}</span>
                              )}
                            </td>
                          </tr>
                        ))}
                        {incollaPreview.ambigui.map((a, i) => (
                          <tr key={`amb-${i}`} style={{ borderTop: "1px solid #eceff1", background: "#fffde7" }}>
                            <td style={{ padding: 6 }}>{a.studente}</td>
                            <td style={{ padding: 6, color: "#e65100", fontWeight: 600 }} colSpan={2}>⚠ Ambiguo: {a.candidati.map(c => `${c.cog} ${c.nom} (${c.classe})`).join(" / ")}</td>
                            <td style={{ padding: 6 }}>{a.delegati.join(", ")}</td>
                          </tr>
                        ))}
                        {incollaPreview.non_trovati.map((a, i) => (
                          <tr key={`nf-${i}`} style={{ borderTop: "1px solid #eceff1", background: "#ffebee" }}>
                            <td style={{ padding: 6 }}>{a.studente}</td>
                            <td style={{ padding: 6, color: "#b71c1c", fontWeight: 600 }} colSpan={2}>✗ Nessun alunno E/F con questo cognome+nome</td>
                            <td style={{ padding: 6 }}>{a.delegati.join(", ")}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {incollaPreview.dry_run ? (
                    <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end", gap: 10 }}>
                      <button data-testid="deleghe-incolla-commit-btn" onClick={() => runIncolla(false)} disabled={incollaBusy || incollaPreview.count_abbinati === 0}
                        style={{ padding: "9px 20px", background: incollaPreview.count_abbinati === 0 ? "#9e9e9e" : "#2e7d32", color: "white", border: "none", borderRadius: 4, fontSize: 13, fontWeight: 700, cursor: incollaBusy ? "wait" : "pointer" }}>
                        {incollaBusy ? "⏳ Importo…" : `✓ Importa ${incollaPreview.count_abbinati} abbinati`}
                      </button>
                    </div>
                  ) : (
                    <div data-testid="deleghe-incolla-done" style={{ marginTop: 12, padding: 10, background: "#e8f5e9", border: "1px solid #81c784", borderRadius: 4, fontSize: 13, color: "#1b5e20", fontWeight: 700 }}>
                      ✓ Import completato: {incollaPreview.slot_importati} delegati scritti su {incollaPreview.alunni_scritti} alunni.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 🆕 Giu 2026: modale "Importa Excel (Classe/Alunno/Delegato)" */}
      {cadOpen && (
        <div data-testid="deleghe-cad-modal" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 6000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 24, overflow: "auto" }}
          onClick={(e) => { if (e.target === e.currentTarget) setCadOpen(false); }}>
          <div style={{ background: "white", borderRadius: 10, width: "min(920px, 96vw)", maxHeight: "92vh", overflow: "auto", boxShadow: "0 12px 40px rgba(0,0,0,0.3)" }}>
            <div style={{ position: "sticky", top: 0, background: "#4527a0", color: "white", padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center", borderTopLeftRadius: 10, borderTopRightRadius: 10 }}>
              <h3 style={{ margin: 0, fontSize: 16 }}>📊 Importa deleghe da Excel/CSV — Primaria + Infanzia (A/E/F)</h3>
              <button onClick={() => setCadOpen(false)} data-testid="deleghe-cad-close" style={{ background: "transparent", border: "none", color: "white", fontSize: 20, cursor: "pointer", lineHeight: 1 }}>✕</button>
            </div>
            <div style={{ padding: 18 }}>
              <p style={{ margin: "0 0 8px", fontSize: 12, color: "#546e7a", lineHeight: 1.5 }}>
                Carica un file <b>.xlsx</b> o <b>.csv</b> con le colonne <b>Classe</b>, <b>Alunno</b>, <b>Delegato</b> (una riga per delega; l'alunno può ripetersi o restare vuoto per i delegati successivi).
                Gli alunni vengono abbinati per <b>cognome + nome</b> (Primaria + Infanzia: A/E/F). Ogni alunno può avere fino a <b>{NUM_SLOTS} delegati</b>.
              </p>
              <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "10px 0", flexWrap: "wrap" }}>
                <input type="file" accept=".xlsx,.csv" data-testid="deleghe-cad-file"
                  onChange={(e) => { setCadFile(e.target.files?.[0] || null); setCadPreview(null); }}
                  style={{ fontSize: 12 }} />
                <button data-testid="deleghe-cad-preview-btn" onClick={() => runCad(true)} disabled={cadBusy || !cadFile}
                  style={{ padding: "8px 16px", background: (cadBusy || !cadFile) ? "#9e9e9e" : "#1565c0", color: "white", border: "none", borderRadius: 4, fontSize: 12, fontWeight: 700, cursor: cadBusy ? "wait" : (cadFile ? "pointer" : "not-allowed") }}>
                  {cadBusy ? "⏳…" : "🔍 Anteprima abbinamenti"}
                </button>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, color: "#455a64" }}
                  title="Se attivo, cancella le deleghe esistenti dell'alunno prima di importare quelle nuove">
                  <input type="checkbox" data-testid="deleghe-cad-overwrite" checked={cadOverwrite} onChange={(e) => setCadOverwrite(e.target.checked)} />
                  Sovrascrivi deleghe esistenti
                </label>
              </div>

              {cadPreview && (
                <div data-testid="deleghe-cad-preview" style={{ marginTop: 6 }}>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10, fontSize: 12 }}>
                    <span style={{ background: "#e8f5e9", color: "#1b5e20", padding: "4px 10px", borderRadius: 20, fontWeight: 700 }}>✓ Abbinati: {cadPreview.count_abbinati}</span>
                    <span style={{ background: "#fff8e1", color: "#e65100", padding: "4px 10px", borderRadius: 20, fontWeight: 700 }}>⚠ Ambigui: {cadPreview.count_ambigui}</span>
                    <span style={{ background: "#ffebee", color: "#b71c1c", padding: "4px 10px", borderRadius: 20, fontWeight: 700 }}>✗ Non trovati: {cadPreview.count_non_trovati}</span>
                  </div>

                  <div style={{ maxHeight: "38vh", overflow: "auto", border: "1px solid #eceff1", borderRadius: 6 }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                      <thead style={{ position: "sticky", top: 0, background: "#37474f", color: "white" }}>
                        <tr>
                          <th style={{ padding: 6, textAlign: "left" }}>Alunno (file)</th>
                          <th style={{ padding: 6, textAlign: "left" }}>Abbinato a</th>
                          <th style={{ padding: 6, textAlign: "center" }}>Classe</th>
                          <th style={{ padding: 6, textAlign: "left" }}>Delegati</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cadPreview.abbinati.map((a, i) => (
                          <tr key={`ok-${i}`} style={{ borderTop: "1px solid #eceff1", background: "#f1f8e9" }}>
                            <td style={{ padding: 6 }}>{a.studente}</td>
                            <td style={{ padding: 6, fontWeight: 600, color: "#1b5e20" }}>{a.cog} {a.nom}</td>
                            <td style={{ padding: 6, textAlign: "center" }}>{a.settore}{a.classe}</td>
                            <td style={{ padding: 6 }}>
                              {a.delegati.join(", ")}
                              {a.troncati && a.troncati.length > 0 && (
                                <span style={{ color: "#e65100", fontWeight: 700 }}> · ⚠ scartati (oltre {NUM_SLOTS}): {a.troncati.join(", ")}</span>
                              )}
                            </td>
                          </tr>
                        ))}
                        {cadPreview.ambigui.map((a, i) => (
                          <tr key={`amb-${i}`} style={{ borderTop: "1px solid #eceff1", background: "#fffde7" }}>
                            <td style={{ padding: 6 }}>{a.studente}</td>
                            <td style={{ padding: 6, color: "#e65100", fontWeight: 600 }} colSpan={2}>⚠ Ambiguo: {a.candidati.map(c => `${c.cog} ${c.nom} (${c.classe})`).join(" / ")}</td>
                            <td style={{ padding: 6 }}>{a.delegati.join(", ")}</td>
                          </tr>
                        ))}
                        {cadPreview.non_trovati.map((a, i) => (
                          <tr key={`nf-${i}`} style={{ borderTop: "1px solid #eceff1", background: "#ffebee" }}>
                            <td style={{ padding: 6 }}>{a.studente}</td>
                            <td style={{ padding: 6, color: "#b71c1c", fontWeight: 600 }} colSpan={2}>✗ Nessun alunno E/F con questo cognome+nome</td>
                            <td style={{ padding: 6 }}>{a.delegati.join(", ")}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {cadPreview.dry_run ? (
                    <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end", gap: 10 }}>
                      <button data-testid="deleghe-cad-commit-btn" onClick={() => runCad(false)} disabled={cadBusy || cadPreview.count_abbinati === 0}
                        style={{ padding: "9px 20px", background: cadPreview.count_abbinati === 0 ? "#9e9e9e" : "#2e7d32", color: "white", border: "none", borderRadius: 4, fontSize: 13, fontWeight: 700, cursor: cadBusy ? "wait" : "pointer" }}>
                        {cadBusy ? "⏳ Importo…" : `✓ Importa ${cadPreview.count_abbinati} abbinati`}
                      </button>
                    </div>
                  ) : (
                    <div data-testid="deleghe-cad-done" style={{ marginTop: 12, padding: 10, background: "#e8f5e9", border: "1px solid #81c784", borderRadius: 4, fontSize: 13, color: "#1b5e20", fontWeight: 700 }}>
                      ✓ Import completato: {cadPreview.slot_importati} delegati scritti su {cadPreview.alunni_scritti} alunni.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
